import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ConversationSummary } from '../../services/conversationService';
import { chatApi, ChatSummaryItem } from '../../services/chatApi';
import { preloadService } from '../../services/preloadService';
import { cacheService } from '../../services/cacheService';
import TopBar from '../TopBar';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import LoadingScreen from '../ui/LoadingScreen';

const ConversationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [conversations, setConversations] = useState<(ConversationSummary | ChatSummaryItem)[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState<'login' | 'register'>('login');

  const [showAuthLoading, setShowAuthLoading] = useState(authLoading);

  // Ensure auth loading shows for at least 800ms to prevent flashing
  useEffect(() => {
    if (authLoading) {
      setShowAuthLoading(true);
    } else {
      const timer = setTimeout(() => {
        setShowAuthLoading(false);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Combined loading state - show loading until auth is verified AND data is fetched
  const isLoading = showAuthLoading || (user && dataLoading);

  // Debug logging
  useEffect(() => {
    console.log('🔍 Conversations loading states:', { 
      authLoading, 
      showAuthLoading, 
      dataLoading, 
      isLoading, 
      hasUser: !!user 
    });
  }, [authLoading, showAuthLoading, dataLoading, isLoading, user]);

  useEffect(() => {
    if (!authLoading && !showAuthLoading) {
      if (user) {
        console.log('👤 User authenticated, loading conversations...');
        loadConversations();
      } else {
        console.log('❌ No user found after auth check');
      }
    }
  }, [user, authLoading, showAuthLoading]);

  const loadConversations = async () => {
    try {
      setDataLoading(true);
      console.log('🔄 Loading conversations...');
      
      // Try to get cached data first for instant loading
      const cachedSummary = cacheService.getChatsSummary();
      if (cachedSummary && cachedSummary.chats) {
        console.log('🚀 Using cached conversations:', cachedSummary.chats.length);
        setConversations(cachedSummary.chats);
        setError(null);
        setDataLoading(false);
        
        // Still fetch fresh data in background to update cache
        chatApi.getChatsSummary(true).catch(() => {
          // Ignore background refresh errors
        });
        return;
      }
      
      const res = await chatApi.getChatsSummary();
      setConversations(res.chats);
      setError(null);
      console.log('✅ Conversations loaded:', res.chats.length);
    } catch (err) {
      console.warn('Conversation service not available:', err);
      setConversations([]);
      setError(null);
    } finally {
      setDataLoading(false);
    }
  };



  const handleSelectConversation = async (conversationId: string) => {
    try {
      // Find the conversation to get companion info
      const conversation = conversations.find(c => 
        ((c as any).id || (c as any).chat_id) === conversationId
      );
      
      if (conversation && (conversation as any).companion_id) {
        // Get companion details to create proper URL
        const companionId = (conversation as any).companion_id;
        const companionName = (conversation as any).companion_name || 'companion';
        
        // Convert name to URL-safe slug
        const nameSlug = companionName.toLowerCase()
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
        
        // Navigate to the proper conversation URL format
        navigate(`/conversations/${nameSlug}/${companionId}?chat=${conversationId}`);
      } else {
        // Fallback to old format if no companion info
        navigate(`/?conversation=${conversationId}`);
      }
    } catch (error) {
      console.error('Error selecting conversation:', error);
      // Fallback to old format
      navigate(`/?conversation=${conversationId}`);
    }
  };

  const handleNewChat = () => {
    navigate('/');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = (now.getTime() - date.getTime()) / (1000 * 60);
    const diffInHours = diffInMinutes / 60;
    const diffInDays = diffInHours / 24;

    if (diffInMinutes < 60) {
      return `${Math.floor(diffInMinutes)}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <TopBar 
        onShowLogin={(mode) => {
          setLoginModalMode(mode || 'login');
          setShowLoginModal(true);
        }}
      />
      
      <div className="flex-1 flex items-center justify-center px-4 pt-16">
        {isLoading ? (
          <LoadingScreen
            title="Loading Conversations"
            message={showAuthLoading ? 'Verifying your account...' : 'Fetching your chat history...'}
          />
        ) : !user ? (
          <div className="text-center max-w-md">
            <div className="text-6xl mb-6">💬</div>
            <h1 className="text-3xl font-bold text-white mb-4">Your Conversations</h1>
            <p className="text-dark-300 mb-8">Sign in to view and manage your chat history with AI companions.</p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setLoginModalMode('login');
                  setShowLoginModal(true);
                }}
                className="w-full px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-3 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300"
              >
                Back to Home
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center max-w-4xl w-full">
          {/* Only show header and action buttons if there are conversations */}
          {conversations.length > 0 && (
            <>
              {/* Header */}
              <div className="mb-12">
                <div className="text-6xl mb-6">💬</div>
                <h1 className="text-3xl font-bold text-white mb-4">Your Conversations</h1>
                <p className="text-dark-300 mb-8">Continue your chats or start a new conversation</p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8 max-w-md mx-auto">
                <button
                  onClick={handleNewChat}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">✨</span>
                  Start New Chat
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-4 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">🏠</span>
                  Back to Home
                </button>
              </div>
            </>
          )}

          {dataLoading ? (
            <LoadingScreen
              title="Loading Conversations"
              message="Fetching your chat history..."
              variant="inline"
              size="md"
            />
          ) : error ? (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-center max-w-md mx-auto">
              <div className="text-4xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-red-400 mb-2">Error Loading Conversations</h3>
              <p className="text-red-300 mb-4">{error}</p>
              <button
                onClick={loadConversations}
                className="px-6 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-all duration-300"
              >
                Try Again
              </button>
            </div>
          ) : conversations.length === 0 ? (
            <div className="text-center">
              <div className="text-6xl mb-6">💭</div>
              <h3 className="text-2xl font-semibold text-white mb-4">No conversations yet</h3>
              <p className="text-dark-300 mb-8 max-w-md mx-auto">
                Start your first conversation with an AI companion and it will appear here.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <button
                  onClick={handleNewChat}
                  className="flex-1 px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">✨</span>
                  Start Your First Chat
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-4 bg-dark-700 text-white rounded-xl font-medium hover:bg-dark-600 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <span className="text-xl">🏠</span>
                  Back to Home
                </button>
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-4">
              {conversations.map((conversation) => (
                <div
                  key={(conversation as any).id || (conversation as any).chat_id}
                  onClick={() => handleSelectConversation((conversation as any).id || (conversation as any).chat_id)}
                  onMouseEnter={() => {
                    // Preload chat data on hover for instant loading
                    const chatId = (conversation as any).id || (conversation as any).chat_id;
                    preloadService.preloadChatData(chatId);
                    
                    // Also preload companion images if available
                    const companionId = (conversation as any).companion_id;
                    if (companionId) {
                      preloadService.preloadCompanionImages(companionId);
                    }
                  }}
                  className="group bg-dark-800/50 backdrop-blur-sm border border-dark-600 rounded-xl p-6 cursor-pointer hover:border-accent-purple/50 hover:bg-dark-800/80 transition-all duration-300 text-left"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-gradient-to-r from-accent-purple to-accent-pink rounded-full flex items-center justify-center">
                          <span className="text-lg">🤖</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white group-hover:text-accent-purple transition-colors">
                            {(conversation as any).title || (conversation as any).companion_name || `Chat with ${(conversation as any).chatbot_name || 'Companion'}`}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-dark-300">
                            <span>{(conversation as any).chatbot_name || (conversation as any).companion_name || 'Companion'}</span>
                            <span>•</span>
                            <span>{formatRelativeTime((conversation as any).updated_at || (conversation as any).latest_message_timestamp || (conversation as any).created_at)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {(conversation as any).last_message_preview || (conversation as any).latest_message_content ? (
                        <div className="bg-dark-700/50 rounded-lg p-3 mb-3">
                          <p className="text-dark-200 text-sm line-clamp-2">
                            {(conversation as any).last_message_preview || (conversation as any).latest_message_content}
                          </p>
                        </div>
                      ) : null}
                      
                      <div className="flex items-center gap-4 text-xs text-dark-400">
                        <span className="flex items-center gap-1">
                          <span>📅</span>
                          {formatDate((conversation as any).created_at)}
                        </span>
                        {(conversation as any).message_count && (
                          <span className="flex items-center gap-1">
                            <span>💬</span>
                            {(conversation as any).message_count} messages
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        )}
      </div>

      <Footer />

      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          initialMode={loginModalMode}
        />
      )}
    </div>
  );
};

export default ConversationsPage;